<template>
  <div class="cognito-login">
    <LoadingIndicator v-if="isLoading" :showError="errorMessage.length > 0">
      <div class="error-message">
        {{ errorMessage }}
      </div>
    </LoadingIndicator>
  </div>
</template>

<script setup lang="ts">
import cognitoAuth from "@/shared/access/CognitoAuth";
import { SessionCacheService } from "@/shared/services/SessionCacheService";
import { CacheService } from "@/shared/services/CacheService";
import { useMainStore } from "@/stores/useMainStore";
import { onMounted, ref } from "vue";
import LoadingIndicator from "./LoadingIndicator.vue";

const mainStore = useMainStore();

const isLoading = ref(false);
const errorMessage = ref("");

const clearCache = function () {
  // Clear any existing session cache
  SessionCacheService.clearSessionCache();
  CacheService.removeAllExpired();
  mainStore.resetPersistedState();
};

const hostedUISignIn = async function () {
  try {
    isLoading.value = true;
    errorMessage.value = "";

    // Initiate hosted UI sign in
    await cognitoAuth.signIn();
  } catch (error: any) {
    console.error("Cognito sign in error:", error);
    errorMessage.value = "Failed to initiate sign in. Please try again.";
  } finally {
    isLoading.value = false;
  }
};

const handleAuthCallback = async function () {
  try {
    // Check if we have URL parameters indicating OAuth callback
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");
    const state = urlParams.get("state");

    if (code) {
      isLoading.value = true;

      // Handle the OAuth callback
      const authState = await cognitoAuth.handleLoginRedirect();

      if (authState.value.isAuthenticated) {
        // Redirect to original URI or default page
        const originalUri = cognitoAuth.getOriginalUri() || "/";
        cognitoAuth.removeOriginalUri();
        window.location.href = originalUri;
      }
    }
  } catch (error: any) {
    console.error("Error handling auth callback:", error);
    errorMessage.value = "Authentication failed. Please try signing in again.";
  } finally {
    isLoading.value = false;
  }
};

onMounted(async () => {
  // If already authenticated, redirect to original URI or home page
  await cognitoAuth.initializeAsync();
  if (await cognitoAuth.isAuthenticated()) {
    const originalUri = cognitoAuth.getOriginalUri() || "/";
    cognitoAuth.removeOriginalUri();
    window.location.href = originalUri;
    return;
  }
  clearCache();
  handleAuthCallback();

  hostedUISignIn();
});
</script>

<style lang="scss">
.cognito-login {
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
